<template>
  <b-modal
    :data-cy="dataCy"
    :title="$t('AiTexter.AiTexter')"
    modal-class="ai-texter-modal"
    header-close-content=""
    size="xl"
    v-model="localModel"
    centered
    ref="aiTexterModal"
    @show="loadAndSetData"
    @hide="cancelRequest"
    no-close-on-backdrop
    no-close-on-esc
    @hidden="onModalClosed">
    <template v-if="vm.showContent && isLoadedAll">
      <div v-if="vm.selectedPage == 'start'">
        <div class="ai-texter">
          <label class="font-weight-bold">{{ $t("AiTexter.Article") }}</label>
          <span
            v-if="vm.selectedLanguage && vm.masterData?.[vm.selectedLanguage]"
            data-cy="aiTexterArticleName"
            v-tooltip="vm.masterData?.[vm.selectedLanguage].name"
            class="article-span"
            >{{ vm.masterData?.[vm.selectedLanguage].name }}</span
          >
          <div class="ai-texter-style-selection">
            <b-form-group>
              <div class="ai-texter">
                <label class="font-weight-bold">{{ $t("AiTexter.Style") }}</label>
              </div>
              <div>
                <dropdown
                  v-model="vm.selectedTextGeneratingStyleName"
                  v-tooltip="validationFeedbacks.name.type"
                  :class="{ 'border-danger': validationFeedbacks.name.type == 'error', 'border-warning': validationFeedbacks.name.type == 'warning' }"
                  :items="vm.textGeneratingStyles"
                  value-field="name"
                  text-field="name"
                  class="width-minus-button"
                  :data-cy="vm.textGeneratingStyles && vm.textGeneratingStyles.length > 0 ? 'ai-Texter-textstyles-dropdown' : 'emptyTextStyles'"
                  @input="updateTextGeneratingStyle">
                  <template #item-template="{ entry }">
                    <span v-if="entry"> {{ entry.name }} </span>
                  </template>
                </dropdown>
              </div>
            </b-form-group>
            <div class="infoText">
              <b-alert class="m-alert" show variant="light">
                <div class="m-alert-icon">
                  <Icon :icon="validationFeedbacks.name.type == 'error' ? 'WatcherWarning' : 'Info'" size="11px"></Icon>
                </div>
                <div class="m-alert-content">
                  {{ validationFeedbacks.name.type == "error" ? validationFeedbacks.name.feedback : vm.selectedTextGeneratingStyleText }}
                </div>
              </b-alert>
            </div>
          </div>
          <h5 class="mb-1 informationbase">{{ $t("AiTexter.informationbase") }}</h5>
          <div class="infoText">
            <b-alert class="m-alert" show variant="light">
              <div class="m-alert-icon">
                <Icon icon="Info" size="11px"></Icon>
              </div>
              <div class="m-alert-content">
                {{ $t("AiTexter.informationbaseNotification") }}
              </div>
            </b-alert>
          </div>
          <header class="bg-white p-0 border-0">
            <b-nav tabs>
              <b-nav-item
                v-for="tab in meta.tabs"
                :active="meta.activeTab == tab.name"
                :key="tab.name"
                @click="selectTab(tab.name)"
                :data-cy="'ai-texter-' + tab.name + '-tab'">
                {{ $t(tab.name, 2) }}
              </b-nav-item>
            </b-nav>
          </header>
          <div class="article-editor-card-body card-body position-relative">
            <div v-if="vm.selectedLanguage && meta.activeTab == 'master.data' && vm.masterData?.[vm.selectedLanguage]" class="h-100">
              <b-overlay z-index="20" opacity="0.9" variant="white" no-wrap data-cy="spinnerRight" :show="vm.isLoadingMasterData">
                <template #overlay>
                  <spinner size="48" />
                </template>
              </b-overlay>
              <MasterDataMultiSelectList :items="vm.masterData?.[vm.selectedLanguage]" v-if="!vm.isLoadingMasterData" />
            </div>
            <!-- masterData tab END -->
            <div v-if="meta.activeTab == 'global.attributes'" class="h-100">
              <AttributeMultiSelectList :items="globalAttributesForSelectedLanguage" v-model:selectedItems="vm.selectedGlobalAttributes" />
            </div>
            <!-- globalAttributes tab END -->
            <div v-if="meta.activeTab == 'text.attributes'" class="h-100">
              <AttributeMultiSelectList
                :items="textAttributesForSelectedLanguage"
                v-model:selectedItems="vm.selectedInvariantAttributes"
                :isText="true"
                :isAiTexterOpen="props.value" />
            </div>
            <!-- invariantAttributes tab END -->
            <div v-if="meta.activeTab == 'table.attributes'" class="h-100">
              <AttributeMultiSelectList :items="tableAttributesSelectedLanguage" v-model:selectedItems="vm.selectedVariantAttributes" />
            </div>
            <!-- variantAttributes tab END -->
            <div v-if="meta.activeTab == 'erp.attributes'" class="h-100">
              <ErpMultiSelectList :items="vm.serializedErpData" v-model:selectedItems="vm.selectedErpAttributes" />
            </div>
            <!-- ERP Attributes tab END -->
          </div>
        </div>
      </div>
      <div v-if="vm.selectedPage == 'generate'">
        <div class="ai-texter">
          <label class="font-weight-bold">{{ $t("AiTexter.Article") }}</label>
          <span v-if="vm.selectedLanguage && vm.masterData?.[vm.selectedLanguage]" v-tooltip="vm.masterData?.[vm.selectedLanguage].name" class="article-span">{{
            vm.masterData?.[vm.selectedLanguage].name
          }}</span>
        </div>
        <div class="ai-texter">
          <b-row>
            <b-col cols="6">
              <b-form-group>
                <!-- Target 1 -->
                <div class="ai-texter">
                  <label class="font-weight-bold">{{ $t("AiTexter.Target") }}</label>
                </div>
                <div>
                  <dropdown
                    v-model="vm.selectedTextGeneratingTargetName"
                    v-tooltip="validationFeedbacks.name.type"
                    value-field="name"
                    text-field="translatedName"
                    :items="vm.targetSectionsNames"
                    class="width-minus-button"
                    data-cy="ai-Texter-target-selection-dropdown"
                    @input="updateTextGeneratingTarget">
                    <template #item-template="{ entry }">
                      <span v-if="entry.name && typeof entry.name === 'string' && vm.selectedLanguage && vm.erpSections?.[vm.selectedLanguage]">
                        {{
                          vm.erpSections?.[vm.selectedLanguage]?.some((section) => section.name === entry.name)
                            ? `${$t("AiTexter.ERPSection")} ${entry.name}`
                            : vm.targetSelectionList.includes(entry.name)
                              ? $t(`AiTexter.${entry.name}`)
                              : entry.name
                        }}
                      </span>
                    </template>
                    <template #default="{ selectedItem, selectedText: text }">
                      <span v-if="selectedItem && vm.selectedLanguage && vm.erpSections?.[vm.selectedLanguage]">
                        {{
                          vm.erpSections?.[vm.selectedLanguage]?.some((section) => section.name === selectedItem.name)
                            ? `${$t("AiTexter.ERPSection")} ${selectedItem.name}`
                            : vm.targetSelectionList.includes(selectedItem.name)
                              ? $t(`AiTexter.${selectedItem.name}`)
                              : text
                        }}
                      </span>
                    </template>
                  </dropdown>
                </div>
              </b-form-group>
              <div class="field-container">
                <div class="ai-texter-table" data-cy="ai-texter-table">
                  <div class="ai-texter-table-header">
                    <div class="ai-texter-table-col ai-texter-target-table-col">
                      <span class="font-weight-bold">{{ $t("AiTexter.Field") }}</span>
                    </div>
                  </div>
                  <div v-if="vm.selectedTarget !== 'masterData' && vm.tableRows">
                    <div v-for="(row, index) in vm.tableRows" :key="`$tablerow-${index}`">
                      <div v-if="(isErpSectionItem(row) || isAttributeItem(row)) && row.name !== undefined && row.name !== null" class="ai-texter-table-row">
                        <div class="ai-texter-table-row-container">
                          <div
                            class="ai-texter-table-row-data"
                            :class="{ 'selected-row': vm.selectedAttributeToTarget == row }"
                            @click="setCurrentAttributePath(row)">
                            <div class="ai-texter-table-col ai-texter-target-table-col" :data-cy="row.name">
                              <span v-tooltip="row.name">{{ row.name }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-if="vm.selectedTarget == 'masterData'">
                    <div class="ai-texter-table-row">
                      <div class="ai-texter-table-row-container">
                        <div
                          v-if="vm.selectedLanguage && vm.masterData?.[vm.selectedLanguage]"
                          data-cy="masterDataDescriptionTarget"
                          class="ai-texter-table-row-data"
                          :class="{
                            'selected-row':
                              vm.selectedAttributeToTarget == vm.masterData?.[vm.selectedLanguage].description && vm.selectedMasterDataParameter !== 'name'
                          }"
                          @click="setCurrentMasterDataPath(vm.masterData?.[vm.selectedLanguage].description, 'description')">
                          <div class="ai-texter-table-col ai-texter-target-table-col" data-cy="select-target-master-data-description">
                            <span>{{ $t("AiTexter.Description") }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="ai-texter-table-row">
                      <div class="ai-texter-table-row-container">
                        <div
                          v-if="vm.selectedLanguage && vm.masterData?.[vm.selectedLanguage]"
                          class="ai-texter-table-row-data"
                          :class="{
                            'selected-row':
                              vm.selectedAttributeToTarget == vm.masterData?.[vm.selectedLanguage].name && vm.selectedMasterDataParameter !== 'description'
                          }"
                          @click="setCurrentMasterDataPath(vm.masterData?.[vm.selectedLanguage].name, 'name')">
                          <div class="ai-texter-table-col ai-texter-target-table-col" data-cy="select-target-master-data-name">
                            <span>{{ $t("AiTexter.Name") }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </b-col>
            <b-col cols="6">
              <label class="font-weight-bold">{{ $t("AiTexter.CurrentValue") }}</label>
              <b-form-group>
                <div class="current-value-box-generate-page">
                  {{ vm.currentValueOfItem }}
                </div>
              </b-form-group>
            </b-col>
          </b-row>
        </div>
      </div>
      <div v-if="vm.selectedPage == 'generateAiText'">
        <div class="ai-texter">
          <label class="font-weight-bold">{{ $t("AiTexter.Article") }}</label>
          <span v-if="vm.selectedLanguage && vm.masterData?.[vm.selectedLanguage]" v-tooltip="vm.masterData?.[vm.selectedLanguage].name" class="article-span">{{
            vm.masterData?.[vm.selectedLanguage].name
          }}</span>
        </div>
        <div class="ai-texter">
          <label class="font-weight-bold">{{ $t("AiTexter.Target") }} </label>
          <span class="ai-texter-target-value"
            >{{
              targetSelectionList.includes(vm.selectedTextGeneratingTargetName)
                ? `${$t("AiTexter." + vm.selectedTextGeneratingTargetName)}`
                : vm.selectedTextGeneratingTargetName
            }}
            /
            {{
              vm.selectedTextGeneratingTargetName === "masterData" && vm.selectedMasterDataParameter
                ? `${$t("AiTexter." + _.capitalize(vm.selectedMasterDataParameter))}`
                : !_.isString(vm.selectedAttributeToTarget)
                  ? vm.selectedAttributeToTarget?.name
                  : vm.selectedAttributeToTarget
            }}
          </span>
        </div>
        <div class="d-flex current-value-wrapper">
          <div class="drawer" :class="{ collapsed: !vm.isDrawerOpen, 'collapsed-area-left': !vm.isDrawerOpen, 'border-right': vm.isDrawerOpen }">
            <div class="drawer-content">
              <b-form-group class="current-value-box-form">
                <template #label> {{ $t("AiTexter.CurrentValue") }} </template>
                <div class="current-value-box">
                  {{ vm.currentValueOfItem }}
                </div>
              </b-form-group>
            </div>
            <div class="drawer-collapse-button" @click="vm.isDrawerOpen = !vm.isDrawerOpen">
              <span class="drawer-collapse-button-icon">
                <Icon v-if="vm.isDrawerOpen" icon="ChevronLeft" />
                <Icon v-show="!vm.isDrawerOpen" icon="ChevronRight" />
              </span>
              <span v-show="!vm.isDrawerOpen" class="drawer-collapse-button-text">{{ $t("AiTexter.CurrentValue") }}</span>
            </div>
          </div>
          <div class="generated-value-area drawer-sibling">
            <label class="font-weight-bold">{{ $t("AiTexter.GeneratedValue") }}</label>
            <HTMLEditor v-show="!vm.isGenerating" v-model="vm.editableText" data-cy="ai-texter-tiny-mce" />
            <div class="spinner-container" v-show="vm.isGenerating">
              <spinner size="48" data-cy="spinner" />
            </div>
          </div>
        </div>
      </div>
    </template>
    <!-- Button area start -->
    <template #modal-footer="{ cancel }">
      <template v-if="vm.showContent && isLoadedAll">
        <div class="footer w-100">
          <div class="footer-actions">
            <div class="row" v-if="vm.selectedPage == 'start'">
              <div class="col d-flex justify-content-end">
                <b-btn @click="switchPageNext" data-cy="ai-texter-next-btn" variant="primary">{{ $t("AiTexter.Next") }}</b-btn>
                <b-btn class="mr-0" @click="cancel" data-cy="cancel-ai-texter">{{ $t("AiTexter.Cancel") }}</b-btn>
              </div>
            </div>
            <div class="row" v-if="vm.selectedPage == 'generate'">
              <div class="col">
                <b-btn class="ml-0" @click="switchPageBack"> <Icon icon="ChevronLeft" class="mr-1 backBtn"></Icon>{{ $t("AiTexter.Back") }}</b-btn>
              </div>
              <div class="col d-flex justify-content-end">
                <b-btn @click="openGenerateAiTextPage()" data-cy="ai-texter-generate-btn" variant="primary">{{ $t("AiTexter.Generate") }}</b-btn>
                <b-btn class="mr-0" @click="cancel">{{ $t("AiTexter.Cancel") }}</b-btn>
              </div>
            </div>
            <div class="row" v-if="vm.selectedPage == 'generateAiText'">
              <div class="col">
                <b-btn class="ml-0" @click="switchPageBack"><Icon icon="ChevronLeft" class="mr-1 backBtn"></Icon>{{ $t("AiTexter.Back") }}</b-btn>
              </div>
              <div class="col d-flex justify-content-end">
                <b-btn :disabled="vm.isGenerating" variant="primary" data-cy="ai-texter-apply-text" @click="sendResult()">{{ $t("AiTexter.Apply") }}</b-btn>
                <b-btn class="mr-0" @click="cancel">{{ $t("AiTexter.Cancel") }}</b-btn>
              </div>
            </div>
          </div>
        </div>
      </template>
    </template>
    <!-- Button area end -->
  </b-modal>
  <warningModal id="warning-modal" v-model:show-modal="vm.showWarning" :title="vm.apiErrorTitle" :message="vm.apiErrorMessage"> </warningModal>
  <NotificationModal v-model:show-modal="vm.legalInformationModal" @notificationStatus="setNotificationStatus"> </NotificationModal>
</template>

<script lang="ts" setup>
import { useArticleActions, useArticleState } from "@ContentEditor/store/composable/useArticle";
import Dropdown from "@common/components/Dropdown.vue";
import HTMLEditor from "@common/components/HTMLEditor.vue";
import Icon from "@common/components/Icon.vue";
import Spinner from "@common/components/Spinner.vue";
import warningModal from "@common/components/WarningModal.vue";
import { useContentLanguages } from "@common/composable/useMetaDataStore";
import { useSaveState, useUserSettings } from "@common/composable/useRootStore";
import { AiTexterTypes } from "@medapro-types/ContentEditor/Components/AiTexter";
import { ArticleMasterDataResultModel } from "@medapro-types/ContentEditor/content-editor-articles";
import { ApiErrorData, ValidationFeedbackEntry } from "@medapro-types/common/common";
import { RootActions } from "@medapro-types/common/rootstate";
import aiTexterApi from "@services/aiTexter";
import attributeGroupsApi from "@services/attributeGroups.js";
import catalogNodesApi from "@services/catalogNodes.js";
import ruleEvaluator from "@util/validations/ruleEvaluator";
import { AxiosError } from "axios";
import _ from "lodash";
import emitter from "tiny-emitter/instance";
import { Editor as TinymceEditor } from "tinymce";
import { computed, nextTick, onMounted, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useActions } from "vuex-composition-helpers";
import AttributeMultiSelectList from "./AITexter/AttributeMultiSelectList.vue";
import ErpMultiSelectList from "./AITexter/ErpMultiSelectList.vue";
import MasterDataMultiSelectList from "./AITexter/MasterDataMultiSelectList.vue";
import NotificationModal from "./AITexter/NotificationModal.vue";

const { t: $t } = useI18n();
const props = defineProps<{ value: boolean; language: string; dataCy?: string }>();
const emits = defineEmits(["input"]);
const aiTexterModal = ref();
const editor = ref<TinymceEditor | null>(null);

//#region StoreData
const { userSettings } = useUserSettings();
const { saveUserSettings } = useActions<RootActions>(["saveUserSettings"]);

const { addSavingPromise, getSavePromiseResultAndResolve } = useSaveState();

const { aiTexterStyles, masterdata, globalAttributes, variantAttributes, invariantAttributes, erpSections, metadata } = useArticleState();
const { saveErpSections, saveMasterData, saveGlobalAttributes, saveInvariantAttributes, saveVariantAttributes } = useArticleActions();
const { loadMasterDataIfNecessary, loadGlobalAttributes, loadVariantAttributes, loadInvariantAttributes, loadErpAttributes } = useArticleActions();
const { languageCodes, primaryLanguageCode, contentLanguages } = useContentLanguages();
const targetSelectionList = ["masterData", "invariantAttributes", "variantAttributes", "globalAttributes"];

const meta = ref({
  activeTab: "master.data",
  tabs: [
    { name: "master.data", show: true },
    { name: "global.attributes", show: true },
    { name: "text.attributes", show: true },
    { name: "table.attributes", show: true },
    { name: "erp.attributes", show: true }
  ]
});

const actionExecuted = ref(false);

type ViewModel = {
  isBusy: boolean;
  showContent: boolean;
  abortController: AbortController | null;
  apiErrorTitle: string | AxiosError;
  apiErrorMessage: string | ApiErrorData;
  currentValueOfItem: string | undefined;
  dataLanguage: string;
  dontShowMessageAgain: boolean;
  editableText: string;
  targetSectionsNames: AiTexterTypes.TargetSection[];
  erpSections: Record<string, AiTexterTypes.ErpSection[]> | null;
  globalAttributes: Record<string, AiTexterTypes.AttributeItem[]> | null;
  invariantAttributes: Record<string, AiTexterTypes.AttributeItem[]> | null;
  isDrawerOpen: boolean;
  isGenerating: boolean;
  isLoadingMasterData: boolean;
  legalInformationModal: boolean;
  masterData: Record<string, ArticleMasterDataResultModel> | null;
  selectedAttributeToTarget: AiTexterTypes.AttributeItem | AiTexterTypes.ErpSectionItem | string | null | undefined;
  selectedDropdownValue: string | null;
  selectedErpAttributes: AiTexterTypes.SelectedErpSectionItem[];
  selectedGlobalAttributes: AiTexterTypes.AttributeItem[];
  selectedInvariantAttributes: AiTexterTypes.AttributeItem[];
  selectedVariantAttributes: AiTexterTypes.AttributeItem[];
  selectedLanguage: string | null;
  selectedMasterData: AiTexterTypes.SelectedMasterData[];
  selectedMasterDataParameter: "name" | "description" | null;
  selectedTarget: (keyof ViewModel & ("globalAttributes" | "variantAttributes" | "invariantAttributes" | "masterData")) | string;
  selectedTextGeneratingStyleName: string;
  selectedTextGeneratingStyleOriginalText: string;
  selectedTextGeneratingStyleText: string;
  selectedTextGeneratingTargetName: string;
  serializedErpData: unknown;
  selectedPage: string;
  showWarning: boolean;
  tableRows: AiTexterTypes.AttributeItem[] | AiTexterTypes.ErpSectionItem[] | AiTexterTypes.MasterDataItem[];
  targetSelectionList: string[];
  textGeneratingStyles: AiTexterTypes.TextGeneratingStyle[];
  translationLanguage: string | null;
  variantAttributes: Record<string, AiTexterTypes.AttributeItem[]> | null;
};

const vm = reactive<ViewModel>({
  isBusy: false,
  showContent: false,
  abortController: null,
  apiErrorTitle: "",
  apiErrorMessage: "",
  currentValueOfItem: "",
  dataLanguage: primaryLanguageCode.value,
  dontShowMessageAgain: userSettings.value.AITexterLegalInformationAccepted === "true",
  editableText: "",
  targetSectionsNames: [],
  erpSections: null,
  globalAttributes: null,
  invariantAttributes: null,
  isDrawerOpen: true,
  isGenerating: false,
  isLoadingMasterData: false,
  legalInformationModal: false,
  masterData: null,
  selectedAttributeToTarget: null,
  selectedDropdownValue: null,
  selectedErpAttributes: [],
  selectedGlobalAttributes: [],
  selectedInvariantAttributes: [],
  selectedVariantAttributes: [],
  selectedLanguage: null,
  selectedMasterData: [],
  selectedMasterDataParameter: null,
  selectedTarget: "masterData",
  selectedTextGeneratingStyleName: "",
  selectedTextGeneratingStyleOriginalText: "",
  selectedTextGeneratingStyleText: "",
  selectedTextGeneratingTargetName: "masterData",
  serializedErpData: {},
  selectedPage: "start",
  showWarning: false,
  tableRows: [],
  targetSelectionList: targetSelectionList,
  textGeneratingStyles: [],
  translationLanguage: languageCodes.value[1] ?? null,
  variantAttributes: null
});

const onModalClosed = () => {
  actionExecuted.value = false;
};

const localModel = computed({
  get() {
    return props.value;
  },
  set(nv) {
    emits("input", nv);
  }
});

const globalAttributesForSelectedLanguage = computed(() => {
  if (vm.selectedLanguage) {
    return vm.globalAttributes?.[vm.selectedLanguage] ?? [];
  } else {
    return [];
  }
});
const textAttributesForSelectedLanguage = computed(() => {
  if (vm.selectedLanguage) {
    return vm.invariantAttributes?.[vm.selectedLanguage];
  } else {
    return [];
  }
});
const tableAttributesSelectedLanguage = computed(() => {
  if (vm.selectedLanguage) {
    return vm.variantAttributes?.[vm.selectedLanguage];
  } else {
    return [];
  }
});

const fallbackLanguageCode = computed(() => {
  for (const lang of contentLanguages.value.enabled) {
    if (lang.languageCode === vm.selectedLanguage) {
      return lang.fallbackLanguageCode;
    }
  }

  return undefined;
});

watch(
  () => userSettings,
  () => {
    vm.dataLanguage = contentLanguages.value.enabled[0].languageCode;
    vm.translationLanguage = contentLanguages.value.enabled[1]?.languageCode ?? null;
  },
  { deep: true }
);

const loadAndSetData = async () => {
  await getSavePromiseResultAndResolve();

  const _loadPromises: Promise<unknown>[] = [];
  const articleId = metadata.value.articleId;
  for (const lang of languageCodes.value) {
    _loadPromises.push(loadMasterDataIfNecessary({ articleId: articleId, language: lang }));
    _loadPromises.push(loadGlobalAttributes({ articleId: articleId, language: lang }));
    _loadPromises.push(loadVariantAttributes({ articleId: articleId, language: lang }));
    _loadPromises.push(loadInvariantAttributes({ articleId: articleId, language: lang }));
    _loadPromises.push(loadErpAttributes({ articleId: articleId, language: lang }));
  }

  await Promise.all(_loadPromises);

  vm.masterData = _.cloneDeep(masterdata.value);
  vm.globalAttributes = _.cloneDeep(globalAttributes.value);
  vm.variantAttributes = _.cloneDeep(variantAttributes.value);
  vm.invariantAttributes = _.cloneDeep(invariantAttributes.value);
  vm.erpSections = _.cloneDeep(erpSections.value);

  if (props.language == "dataLanguage") {
    vm.selectedLanguage = vm.dataLanguage;
  }
  if (props.language == "translationLanguage") {
    vm.selectedLanguage = vm.translationLanguage;
  }

  vm.selectedPage = "start";
  vm.selectedTarget = "masterData";
  meta.value.activeTab = "master.data";
  vm.targetSectionsNames = [
    { id: 1, name: "masterData", translatedName: $t("AiTexter.masterData") },
    { id: 2, name: "invariantAttributes", translatedName: $t("AiTexter.invariantAttributes") },
    { id: 3, name: "variantAttributes", translatedName: $t("AiTexter.variantAttributes") },
    { id: 4, name: "globalAttributes", translatedName: $t("AiTexter.globalAttributes") }
  ];

  await constructMasterData();
  constructErpSections();
  // Now that the fallback names are considered, we check if have erp sections to include
  if (vm.selectedLanguage && vm.erpSections?.[vm.selectedLanguage]) {
    vm.erpSections?.[vm.selectedLanguage].forEach((section, index) => {
      const exists = vm.targetSectionsNames.some((item) => item.name == section.name);
      if (!exists) {
        if (section.erpAttributes?.length && section.name !== "") {
          vm.targetSectionsNames.push({ id: 5 + index, name: section.name, translatedName: section.name });
        }
      }
    });
  }

  constructAttributes();
  if (vm.selectedLanguage && vm.masterData?.[vm.selectedLanguage]) {
    setCurrentMasterDataPath(vm.masterData[vm.selectedLanguage].name, "name");
  }
  getTextStylesFromApi();
  vm.selectedTextGeneratingTargetName = "masterData";
};

const isLoadedAll = computed(() => {
  return !!vm.masterData && !!vm.globalAttributes && !!vm.variantAttributes && !!vm.invariantAttributes && !!vm.erpSections;
});

watch(
  () => isLoadedAll.value,
  (nv) => {
    nextTick(() => {
      vm.showContent = nv;
    });
  }
);

const arrayToString = (array: unknown) => {
  if (!Array.isArray(array)) {
    return "";
  }
  return array.filter((item) => item !== "" && item !== null).join("/");
};
const constructMasterData = async () => {
  vm.isLoadingMasterData = true;
  try {
    if (vm.selectedLanguage) {
      const masterDataOfSelectedLanguage = vm.masterData?.[vm.selectedLanguage];
      if (!masterDataOfSelectedLanguage) {
        return;
      }

      const fetchedCatalogNodePathInBaseCatalog: AiTexterTypes.CatalogPathItem[] = await catalogNodesApi.getCatalogNodePathInBaseCatalog(
        masterDataOfSelectedLanguage.variationGroupId,
        vm.selectedLanguage
      );
      const fetchedAttributeGroupsApiPath: AiTexterTypes.AttributeGroupPathItem[] = await attributeGroupsApi.getPath(
        vm.selectedLanguage,
        masterDataOfSelectedLanguage.attributeGroupId
      );

      const catalogGroupPath = fetchedCatalogNodePathInBaseCatalog.map((obj) => {
        return obj.name;
      });

      const attributeGroupPath = fetchedAttributeGroupsApiPath.filter((obj) => obj.name !== "").map((obj) => obj.name);
      Object.assign(masterDataOfSelectedLanguage, {
        catalogGroupPath: arrayToString(catalogGroupPath),
        attributeGroupPath: arrayToString(attributeGroupPath)
      });

      if (
        (masterDataOfSelectedLanguage.name === "" ||
          masterDataOfSelectedLanguage.description === "" ||
          masterDataOfSelectedLanguage.variationGroupName === "") &&
        fallbackLanguageCode.value
      ) {
        const masterDataKeys: ["description", "name", "variationGroupName"] = ["description", "name", "variationGroupName"];
        masterDataKeys.forEach((attrType) => {
          if (vm.masterData && !masterDataOfSelectedLanguage[attrType]) {
            masterDataOfSelectedLanguage[attrType] = fallbackLanguageCode.value ? vm.masterData[fallbackLanguageCode.value][attrType] : "";
          }
        });
      }
    }
  } catch (error) {
    console.error("An Error has occured during loading master data.", error);
  } finally {
    vm.isLoadingMasterData = false;
  }
};
const constructAttributes = () => {
  if (fallbackLanguageCode.value) {
    const possibleAttributes: Array<"globalAttributes" | "variantAttributes" | "invariantAttributes"> = [
      "globalAttributes",
      "variantAttributes",
      "invariantAttributes"
    ];

    const possibleAttributeKeys: Array<keyof AiTexterTypes.AttributeItem & ("name" | "value" | "unit")> = ["name", "value", "unit"];

    possibleAttributes.forEach((attrType) => {
      if (vm[attrType] && vm.selectedLanguage) {
        vm[attrType]?.[vm.selectedLanguage].forEach((x, index) => {
          possibleAttributeKeys.forEach((key) => {
            if (x[key] === "" && fallbackLanguageCode.value) {
              x[key] = vm[attrType]?.[fallbackLanguageCode.value][index][key] ?? "";
            }
          });
        });
      }
    });
  }
};

const constructErpSections = () => {
  if (vm.selectedLanguage) {
    const serializedErpData = vm.erpSections?.[vm.selectedLanguage]
      .map((erpSection, sectionIndex) => {
        const fallbackSection = fallbackLanguageCode.value ? vm.erpSections?.[fallbackLanguageCode.value]?.[sectionIndex] : undefined;
        return erpSection.erpAttributes?.map((erpAttributes, attrIndex) => {
          const fallbackAttributes = fallbackSection?.erpAttributes?.[attrIndex];
          return {
            erpSectionId: erpSection.erpSectionId,
            erpSectionName: erpSection.name || fallbackSection?.name || "",
            erpAttributeName: erpAttributes.name || fallbackAttributes?.name || "",
            erpAttributeValue: erpAttributes.value || fallbackAttributes?.value || "",
            erpAttributeUnit: erpAttributes.unit || fallbackAttributes?.unit || "",
            erpAttributeType: erpAttributes.userType || fallbackAttributes?.userType || ""
          };
        });
      })
      .flat();

    //Set fallback language erp attribute names to selected language erp attribute names if empty
    vm.erpSections?.[vm.selectedLanguage].map((erpSection, sectionIndex) => {
      const fallbackSection = fallbackLanguageCode.value ? vm.erpSections?.[fallbackLanguageCode.value]?.[sectionIndex] : undefined;
      erpSection.name = erpSection.name || fallbackSection?.name || "";
      return erpSection.erpAttributes?.map((erpAttributes, attrIndex) => {
        const fallbackAttributes = fallbackSection?.erpAttributes?.[attrIndex];
        erpAttributes.name = erpAttributes.name || fallbackAttributes?.name || "";
      });
    });
    vm.serializedErpData = serializedErpData;
  }
};

const selectTab = (tab: string) => {
  meta.value.activeTab = tab;
};

const setCurrentAttributePath = (currentValue: AiTexterTypes.AttributeItem | AiTexterTypes.ErpSectionItem | undefined) => {
  if (!currentValue) return;
  vm.selectedAttributeToTarget = currentValue;
  vm.currentValueOfItem = currentValue.value;
};

const setCurrentMasterDataPath = (currentValue: string | undefined, masterDataParameter: "name" | "description") => {
  vm.selectedMasterDataParameter = masterDataParameter;
  vm.selectedAttributeToTarget = currentValue;
  vm.currentValueOfItem = currentValue;
};

const sendResult = async () => {
  if (vm.selectedTarget === "masterData") {
    await handleMasterDataUpdate();
    emits("input", false);
    return;
  }

  if (["invariantAttributes", "globalAttributes", "variantAttributes"].includes(vm.selectedTarget)) {
    await handleAttributeUpdate();
    emits("input", false);
    return;
  }
  if (vm.selectedAttributeToTarget && !_.isString(vm.selectedAttributeToTarget)) {
    vm.selectedAttributeToTarget.value = vm.editableText;
    contentLanguages.value.enabled.forEach((lang) =>
      addSavingPromise(
        saveErpSections({
          language: lang.languageCode,
          model: vm.erpSections?.[lang.languageCode]
        })
      )
    );
    await getSavePromiseResultAndResolve();
    emits("input", false);
  }
};

const handleMasterDataUpdate = async () => {
  if (vm.selectedLanguage) {
    if (!vm.selectedMasterDataParameter || !["name", "description"].includes(vm.selectedMasterDataParameter) || _.isEmpty(vm.masterData)) return;

    vm.masterData[vm.selectedLanguage][vm.selectedMasterDataParameter] = vm.editableText;

    const args = {
      inheritanceOptions: {
        status: false,
        assignedUser: false,
        validity: false
      },
      model: vm.masterData[vm.selectedLanguage]
    };
    addSavingPromise(
      saveMasterData({
        language: vm.selectedLanguage,
        articleData: args
      })
    );

    await getSavePromiseResultAndResolve();
    emitter.emit("reload-after-ai-update");
  }
};

const isAttributeItem = (e: unknown): e is AiTexterTypes.AttributeItem => {
  return !!e && Object.hasOwn(e, "value") && Object.hasOwn(e, "name") && Object.hasOwn(e, "isTranslatable") && Object.hasOwn(e, "attributeId");
};

const isErpSectionItem = (e: unknown): e is AiTexterTypes.ErpSectionItem => {
  return !!e && Object.hasOwn(e, "value") && Object.hasOwn(e, "name") && Object.hasOwn(e, "attributeId");
};

const isSelectedErpSectionItem = (e: unknown): e is AiTexterTypes.SelectedErpSectionItem => {
  return (
    !!e &&
    Object.hasOwn(e, "erpSectionName") &&
    Object.hasOwn(e, "erpAttributeName") &&
    Object.hasOwn(e, "erpAttributeValue") &&
    Object.hasOwn(e, "erpAttributeUnit")
  );
};

const isSelectedAttributeItem = (e: unknown): e is AiTexterTypes.SelectedAttributeItem => {
  return !!e && Object.hasOwn(e, "name") && Object.hasOwn(e, "value") && Object.hasOwn(e, "unit");
};

const handleAttributeUpdate = async () => {
  if (vm.selectedAttributeToTarget && !_.isString(vm.selectedAttributeToTarget) && isAttributeItem(vm.selectedAttributeToTarget) && vm.selectedLanguage) {
    vm.selectedAttributeToTarget.value = vm.editableText;
    const getModel = () => {
      switch (vm.selectedTarget) {
        case "globalAttributes":
          return contentLanguages.value.enabled.map((x) => ({
            language: x.languageCode,
            data: vm.globalAttributes?.[x.languageCode]
          }));
        case "invariantAttributes":
          return contentLanguages.value.enabled.map((x) => ({
            language: x.languageCode,
            data: vm.invariantAttributes?.[x.languageCode]
          }));
        case "variantAttributes":
          return contentLanguages.value.enabled.map((x) => ({
            language: x.languageCode,
            data: vm.variantAttributes?.[x.languageCode]
          }));

        default:
          break;
      }
    };

    const model = getModel();

    switch (vm.selectedTarget) {
      case "globalAttributes":
        model?.forEach((x) => {
          addSavingPromise(
            saveGlobalAttributes({
              language: x.language,
              model: x.data
            })
          );
        });
        break;
      case "invariantAttributes":
        model?.forEach((x) => {
          addSavingPromise(
            saveInvariantAttributes({
              language: x.language,
              model: x.data
            })
          );
        });
        break;
      case "variantAttributes":
        model?.forEach((x) => {
          addSavingPromise(
            saveVariantAttributes({
              language: x.language,
              model: x.data
            })
          );
        });

        break;
    }

    await getSavePromiseResultAndResolve();
    emitter.emit("reload-after-ai-update");
  }
};

const openGenerateAiTextPage = () => {
  if (vm.selectedPage == "generate" && !vm.dontShowMessageAgain) {
    vm.legalInformationModal = true;
  }

  if (vm.dontShowMessageAgain) {
    vm.selectedPage = "generateAiText";
    dataforPrompting();
  }
};

const dataforPrompting = async () => {
  const promptData: AiTexterTypes.PromptData[] = [];
  const attributeMappings = {
    selectedMasterData: "MasterData",
    selectedGlobalAttributes: "GlobalAttribute",
    selectedVariantAttributes: "TableAttribute",
    selectedInvariantAttributes: "TextAttribute",
    selectedErpAttributes: "ErpAttribute"
  };

  Object.entries(attributeMappings).forEach(([key, type]) => {
    const attributeData =
      vm[
        key as keyof ViewModel &
          ("selectedMasterData" | "selectedGlobalAttributes" | "selectedVariantAttributes" | "selectedInvariantAttributes" | "selectedErpAttributes")
      ];

    if (!_.isEmpty(attributeData)) {
      const sourceValues: AiTexterTypes.PromptDataSourceValue[] = attributeData.map((item) => {
        if (isSelectedErpSectionItem(item)) {
          return {
            section: item.erpSectionName || "",
            name: item.erpAttributeName || "",
            value: item.erpAttributeValue || "",
            unit: item.erpAttributeUnit || ""
          };
        } else if (isSelectedAttributeItem(item)) {
          return {
            name: _.isString(item.name) ? item.name : "",
            value: _.isString(item.value) ? item.value : "",
            unit: _.isString(item.unit) ? item.unit : ""
          };
        } else {
          return {
            name: _.isString(item.name) ? item.name : "",
            value: _.isString(item.value) ? item.value : "",
            unit: ""
          };
        }
      });

      promptData.push({ type, sourceValues });
    }
  });

  vm.editableText = "";
  vm.isGenerating = true;
  try {
    vm.abortController = new AbortController();
    if (vm.selectedLanguage) {
      const generatedTextFromApi = await aiTexterApi.generateText(
        vm.selectedLanguage,
        promptData,
        vm.selectedTextGeneratingStyleOriginalText,
        vm.abortController.signal
      );
      vm.editableText = generatedTextFromApi;
    }
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      const errorData: ApiErrorData | undefined = error.response?.data;
      switch (errorData?.error) {
        case "InvalidApiKey":
          vm.apiErrorTitle = $t(`api.errorTitles.InvalidApiKey`);
          vm.apiErrorMessage = $t(`api.errors.InvalidApiKey`);
          break;
        case "InternalServerError":
          vm.apiErrorTitle = $t(`api.errorTitles.InternalServerError`);
          vm.apiErrorMessage = $t(`api.errors.InternalServerError`);
          break;
        case "RateLimitExceeded":
          vm.apiErrorTitle = $t(`api.errorTitles.RateLimitExceeded`);
          vm.apiErrorMessage = $t(`api.errors.RateLimitExceeded`);
          break;
        case "ServiceUnavailable":
          vm.apiErrorTitle = $t(`api.errorTitles.ServiceUnavailable`);
          vm.apiErrorMessage = $t(`api.errors.ServiceUnavailable`);
          break;
        case "InsufficientQuota":
          vm.apiErrorTitle = $t(`api.errorTitles.InsufficientQuota`);
          vm.apiErrorMessage = $t(`api.errors.InsufficientQuota`);
          break;
        case "CanceledError":
          vm.apiErrorTitle = $t(`api.errorTitles.CanceledError`);
          vm.apiErrorMessage = $t(`api.errors.CanceledError`);
          break;
        default:
          vm.apiErrorTitle = $t(`api.errorTitles.FailedToGenerateText`);
          vm.apiErrorMessage = $t(`api.errors.FailedToGenerateText`);
          break;
      }
    }
    vm.showWarning = true;
  } finally {
    vm.isGenerating = false;
  }
};

const cancelRequest = () => {
  if (vm.abortController) {
    vm.abortController.abort();
  }
  aiTexterModal.value?.hide();
};

const updateTextGeneratingStyle = (selectedStyle: string) => {
  const style = vm.textGeneratingStyles?.find((styles) => styles.name === selectedStyle);
  if (style) {
    vm.selectedTextGeneratingStyleName = style.name;
    vm.selectedTextGeneratingStyleText = style.text;
    vm.selectedTextGeneratingStyleOriginalText = style.originalText;
  }
};

const updateTextGeneratingTarget = (selectedTarget: AiTexterTypes.SelectedTarget, triggeredFromNextButton: boolean) => {
  vm.selectedTarget = selectedTarget;
  const target = vm.targetSectionsNames.find((section) => section.name === selectedTarget);

  if (target) {
    vm.selectedTextGeneratingTargetName = target.name;
  }
  if (vm.selectedLanguage) {
    const attributeMapping: AiTexterTypes.AttributeMapping = {
      globalAttributes: vm.globalAttributes?.[vm.selectedLanguage],
      invariantAttributes: vm.invariantAttributes?.[vm.selectedLanguage],
      variantAttributes: vm.variantAttributes?.[vm.selectedLanguage]
    };
    if (selectedTarget in attributeMapping) {
      vm.tableRows = attributeMapping[selectedTarget as keyof AiTexterTypes.AttributeMapping]?.filter((attr) => attr.defaultValueOnly === false) ?? [];
    }
    const tempErpSectionItems: AiTexterTypes.ErpSectionItem[] = [];
    vm.erpSections?.[vm.selectedLanguage].forEach((section) => {
      if (selectedTarget == section.name) {
        section.erpAttributes?.forEach((attributes) => {
          if (attributes.defaultValueOnly === false) tempErpSectionItems.push(attributes);
        });
        vm.tableRows = tempErpSectionItems;
      }
    });

    if (actionExecuted.value && triggeredFromNextButton !== undefined) return;
    const masterDataName = vm.masterData?.[vm.selectedLanguage]?.description;
    if (selectedTarget == "masterData") {
      setCurrentMasterDataPath(masterDataName, "description");
    } else {
      isErpSectionItem(vm.tableRows[0]) || isAttributeItem(vm.tableRows[0]) ? setCurrentAttributePath(vm.tableRows[0]) : "";
    }
  }
  actionExecuted.value = true;
};

function switchPageNext() {
  if (vm.selectedPage == "start") {
    vm.selectedPage = "generate";

    updateTextGeneratingTarget(vm.selectedTarget, true);

    return;
  }
}
function switchPageBack() {
  if (vm.selectedPage == "generate") {
    vm.selectedPage = "start";
    return;
  }
  if (vm.selectedPage == "generateAiText") {
    if (vm.abortController) {
      vm.abortController.abort();
    }
    vm.selectedPage = "generate";
    return;
  }
}

const validationFeedbacks = computed(() => {
  const rules = {
    name: [
      {
        rule: () => _.isEmpty(vm.textGeneratingStyles),
        msg: $t("validation.noStylesAvailable"),
        severity: "error"
      },
      {
        rule: () => typeof vm.selectedTextGeneratingStyleName === "string" && vm.selectedTextGeneratingStyleName.trim() == "",
        msg: $t("validation.stylesAreEmpty"),
        severity: "warning"
      }
    ]
  };

  const feedbacks = <Record<string, ValidationFeedbackEntry>>ruleEvaluator(vm.textGeneratingStyles, rules);
  return feedbacks;
});
async function getTextStylesFromApi() {
  const staticPromptStyles = [
    "MarketingText_short",
    "MarketingText_long",
    "PressNote",
    "ArticleDescriptionMax80Characters",
    "FactualPrompt",
    "FormalPrompt",
    "InformalPrompt",
    "CatchyPrompt",
    "BlogPrompt",
    "SocialMediaWithTagsPrompt",
    "SocialMediaWithoutTagsPrompt",
    "TwitterPrompt",
    "HtmlPrompt",
    "BulletPrompt"
  ];
  try {
    if (vm.selectedLanguage) {
      vm.textGeneratingStyles = aiTexterStyles.value[vm.selectedLanguage].map((style) => {
        if (staticPromptStyles.includes(style)) {
          return {
            name: $t(`AiTexter.PromptStyleName.${style}`),
            text: $t(`AiTexter.PromptStyleText.${style}`),
            originalText: style
          };
        } else {
          return {
            name: style,
            text: "",
            originalText: style
          };
        }
      });

      vm.selectedTextGeneratingStyleName = vm.textGeneratingStyles[0].name;
      vm.selectedTextGeneratingStyleText = vm.textGeneratingStyles[0].text;
      vm.selectedTextGeneratingStyleOriginalText = vm.textGeneratingStyles[0].originalText;
    }
  } catch (err) {
    console.log("err", err);
  }
}

const setNotificationStatus = async (dontShowMessageAgain: boolean) => {
  if (vm.selectedPage == "generate" && !vm.dontShowMessageAgain) {
    vm.legalInformationModal = false;
  }
  vm.dontShowMessageAgain = dontShowMessageAgain;
  await saveUserSettings({ AITexterLegalInformationAccepted: vm.dontShowMessageAgain ? "true" : "false" });
  vm.selectedPage = "generateAiText";
  dataforPrompting();
};

onMounted(() => {
  editor.value?.execCommand("RemoveFormat");
  editor.value?.execCommand("mceRepaint");
});
</script>
